<!--
// nuxt-ui/components/pages/players-player/TeamSeasonPlayersSlider.vue
-->
<script setup lang="ts">
import type { Player, PlayerSeasons, SeasonPlayerInfo } from "~/src/player/domain/Player";
import type { Team } from "~/src/team/domain/models/Team";
import type { SeasonStatsPartner } from "~/src/season-cluster/domain/models/01.Season";
import { arrowsSliderVisibility } from "~/src/Shared/utils";
import { useCompetitionStore } from "~/nuxt-ui/stores/competitionStore";
import CardDraftPlayer from "~/nuxt-ui/components/Shared/players/CardDraftPlayer.vue";
import SliderArrowsBehaviour from "~/nuxt-ui/components/Shared/slider-matches/SliderArrowsBehaviour.vue";
import CardPlayerFeaturedCategory from "~/nuxt-ui/components/Shared/players/CardPlayerFeaturedCategory.vue";

const localePath = useLocalePath();
const { region } = useCompetitionStore();

type Props = {
  team: Team | undefined;
  player: PlayerSeasons;
  teamSeasonPlayers: Player[];
  seasonId: number;
  playerSeasonInfo?: SeasonPlayerInfo;
  isMobile?: boolean;
  seasonMvpPartner?: SeasonStatsPartner;
};
const props = defineProps<Props>();

const displayedPlayersCount = computed(() => (props.isMobile ? 3 : 8));
const filteredTeamSeasonPlayers = computed(() => {
  return props.teamSeasonPlayers
    .filter(teamSeasonPlayer => teamSeasonPlayer.id !== props.player.id)
    .slice(0, displayedPlayersCount.value);
});

const playersContainer = ref<HTMLElement | null>(null);

onMounted(() => {
  const playersContainer: HTMLElement | null = document.getElementById("playersSlider");
  const buttonLeft: HTMLElement | null = document.getElementById("buttonLeft");
  const buttonRight: HTMLElement | null = document.getElementById("buttonRight");
  if (playersContainer) {
    let childrenPlayersContainerWidth: number = 0;
    const childrenPlayersContainer = [...playersContainer.children];
    childrenPlayersContainer.forEach(item => (childrenPlayersContainerWidth += item.clientWidth));

    if (buttonRight && childrenPlayersContainerWidth < window.innerWidth) {
      buttonRight.classList.add("hidden");
    }

    playersContainer.addEventListener("scroll", () => {
      arrowsSliderVisibility("general", playersContainer, buttonLeft, buttonRight);
    });
  }
});
</script>

<template>
  <div class="pb-12 lg:pb-0 lg:relative" v-if="player && teamSeasonPlayers.length > 0 && seasonId">
    <div class="mx-6 mb-4 lg:mb-0 lg:mx-0">
      <p class="kql-page-section-title">
        {{ $t("player.team.season.players.title") }}
      </p>
      <hr />
    </div>

    <slider-arrows-behaviour
      button-right-id="buttonRight"
      button-left-id="buttonLeft"
      :button-scroll-distance="258"
      slider-container-id="playersSlider"
      :conditionally-render-if="!!filteredTeamSeasonPlayers"
    >
      <div
        id="playersSlider"
        ref="playersSlider"
        class="lg:py-12 flex lg:flex-row flex-col lg:gap-4 gap-2 lg:min-w-full overflow-x-auto hide-scrollbar"
      >
        <div class="lg:w-full lg:min-w-[500px]" v-for="teamSeasonPlayer in filteredTeamSeasonPlayers">
          <card-draft-player
            v-if="
              (teamSeasonPlayer.id !== player.id && teamSeasonPlayer.category === 'draft') ||
              !teamSeasonPlayer.category
            "
            :key="teamSeasonPlayer.id"
            :player-team="team"
            :player-complete-name="teamSeasonPlayer.shortName"
            :player-number="teamSeasonPlayer.jersey"
            :player-position="teamSeasonPlayer.role"
            :player-image="teamSeasonPlayer.image"
            :season-id="seasonId"
            :player-id="teamSeasonPlayer.id"
            :player="teamSeasonPlayer"
            :mvp-partner="seasonMvpPartner"
            isTeamSeasonPlayersSlider
            :show-clause="!!seasonId && seasonId !== 28"
          ></card-draft-player>
          <card-player-featured-category
            v-if="
              teamSeasonPlayer.id !== player.id &&
              teamSeasonPlayer.category &&
              teamSeasonPlayer.category === 'wildcard'
            "
            :player-complete-name="teamSeasonPlayer.shortName"
            :player-number="teamSeasonPlayer.jersey"
            :player-team="team"
            :player-image="teamSeasonPlayer.image"
            :season-id="seasonId"
            :player-category="teamSeasonPlayer.category"
            :player-position="teamSeasonPlayer.role"
            :player-id="teamSeasonPlayer.id"
            :player="teamSeasonPlayer"
            :mvp-partner="seasonMvpPartner"
            isTeamSeasonPlayersSlider
          ></card-player-featured-category>
        </div>
      </div>
    </slider-arrows-behaviour>
  </div>
</template>

<style scoped></style>
